import React, { useMemo, useState, useCallback } from 'react';
import { customColor, useStudioVoiceBranding, useStudioVoiceSkills, useWidgetConfig } from '@brandclub/bc-widgets';
import type { ChatInputState, ChatInputPreset } from '@brandclub/bc-widgets';
import type { ConversationAccessPointType } from '../../tracking';
import { useConciergeContext } from '../../ConciergeContextProvider';
import { PromptInputConcierge } from '../PromptInputConcierge';
import { useSupportCopy } from '../SupportThemeProvider';

interface ConciergeInputAreaProps {
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  handleSearch: (text: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => void;
  isActivelyStreaming: boolean;
  conversationHistoryLength: number;
  onTrackAccessPoint?: (accessPoint: ConversationAccessPointType, query: string) => void;
  onStop?: () => void;
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ChatInputState>;
  /** Override which preset definitions to use (e.g. PANEL_CHAT_INPUT_PRESETS for panel inputs) */
  presets?: Record<Exclude<ChatInputPreset, 'custom'>, Partial<ChatInputState>>;
  /** Force mobile-responsive styling regardless of viewport width (for panel/embedded window). */
  forceMobile?: boolean;
  // ─── Live takeover ─────────────────────────────────────────────────────────
  /** True while a human teammate is live-handling this thread. */
  liveTakeoverActive?: boolean;
  /** Display name of the connected teammate (e.g. "Patrick Leake"). */
  adminName?: string;
  /** Optional avatar URL for the teammate. Falls back to a colored initial. */
  adminAvatarUrl?: string;
  /** Disconnect handler. When supplied, the strip renders a Disconnect button. */
  onEndLiveAgent?: () => Promise<void> | void;
}

const DEFAULT_PLACEHOLDER = 'Ask me anything';
const DEFAULT_FOLLOW_UP_PLACEHOLDER = 'Have another question?';

export const ConciergeInputArea: React.FC<ConciergeInputAreaProps> = ({
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  conversationHistoryLength,
  onTrackAccessPoint,
  onStop,
  widgetId = 'concierge-panel-chat-input',
  displayName = 'Chat Input (Conversation)',
  initialState,
  presets,
  forceMobile = false,
  liveTakeoverActive = false,
  adminName,
  adminAvatarUrl,
  onEndLiveAgent,
}) => {
  const chatInputConfig = useWidgetConfig<{
    placeholderDefault?: string;
    placeholderFollowUp?: string;
  }>(widgetId, {});

  // Read font config from the home-page chat input widget so the panel input
  // automatically inherits the brand's configured font (Poppins, custom brand
  // font, etc.).  If the panel widget itself has an explicit fontFamily saved
  // in Studio that will still take priority via the normal saved-config layer.
  const homeInputConfig = useWidgetConfig<{ fontFamily?: string }>('concierge-chat-input', {});

  // D-33 — brand can override the takeover-mode placeholder, footer caption,
  // and "Support Team" label from Concierge Studio → Branding → Support.
  const supportCopy = useSupportCopy();

  const adminFirstName = useMemo(() => (adminName ? adminName.split(' ')[0] : undefined), [adminName]);

  const placeholder = useMemo(() => {
    if (liveTakeoverActive) {
      // Template var `{firstName}` is substituted at render time. When no
      // admin name is known yet, fall back to the legacy "Reply to support…"
      // string instead of leaving an empty `{firstName}` token in the UI.
      if (adminFirstName) {
        return supportCopy.takeoverPlaceholder.replace(/\{firstName\}/g, adminFirstName);
      }
      return 'Reply to support…';
    }
    if (conversationHistoryLength > 0) {
      return chatInputConfig.placeholderFollowUp || DEFAULT_FOLLOW_UP_PLACEHOLDER;
    }
    return chatInputConfig.placeholderDefault || DEFAULT_PLACEHOLDER;
  }, [
    liveTakeoverActive,
    adminFirstName,
    conversationHistoryLength,
    chatInputConfig.placeholderDefault,
    chatInputConfig.placeholderFollowUp,
    supportCopy.takeoverPlaceholder,
  ]);

  const handleSearchWithTracking = useCallback(
    (text: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => {
      onTrackAccessPoint?.('search_bar', text);
      handleSearch(text, files);
    },
    [handleSearch, onTrackAccessPoint],
  );

  // When takeover is active, suppress the input's own border so the merged
  // strip + input frame reads as a single rounded card. The wrapper below
  // draws the unified emerald border instead.
  const mergedInitialState = useMemo<Partial<ChatInputState> | undefined>(() => {
    const base: Partial<ChatInputState> = {
      ...(homeInputConfig.fontFamily ? { fontFamily: homeInputConfig.fontFamily } : {}),
      ...initialState,
    };
    if (liveTakeoverActive) {
      return {
        ...base,
        borderColor: customColor('transparent'),
        borderColorFocus: customColor('transparent'),
        borderWidth: 0,
        shadowEnabled: false,
      };
    }
    return base;
  }, [homeInputConfig.fontFamily, initialState, liveTakeoverActive]);

  const [endPending, setEndPending] = useState(false);
  const handleEnd = useCallback(async () => {
    if (!onEndLiveAgent || endPending) return;
    setEndPending(true);
    try {
      await onEndLiveAgent();
    } finally {
      setEndPending(false);
    }
  }, [onEndLiveAgent, endPending]);

  // Plan 7a / D-63 — mid-conversation CallBenny CTA. Voice is now a
  // full-screen overlay opened from context state; the click handler must
  // run synchronously on the user's tap (no awaits) for iOS Safari mic
  // permission gating. We pass the current conversationId so the overlay
  // resumes the same thread; the opener falls back to the existing id or
  // a fresh UUID. Suppressed during live-takeover so we don't surface
  // voice while a human teammate is on the line.
  const voiceSkills = useStudioVoiceSkills();
  const voiceBranding = useStudioVoiceBranding();
  const { conversationId, openVoiceCall } = useConciergeContext();
  const showVoiceCall = voiceSkills.enabled && !liveTakeoverActive;
  const handleVoiceCallClick = useCallback(() => {
    openVoiceCall(conversationId ?? undefined);
  }, [openVoiceCall, conversationId]);

  const promptInput = (
    <PromptInputConcierge
      placeholder={placeholder}
      value={searchInputValue}
      onChange={setSearchInputValue}
      onSubmit={handleSearchWithTracking}
      isStreaming={isActivelyStreaming}
      onStop={onStop}
      widgetId={widgetId}
      displayName={displayName}
      presets={presets}
      forceMobile={forceMobile}
      initialState={mergedInitialState}
      showCallButton={showVoiceCall}
      onCallClick={showVoiceCall ? handleVoiceCallClick : undefined}
      voiceAssistantName={voiceBranding.persona.assistantName}
    />
  );

  return (
    <div
      className="tw:flex tw:shrink-0 tw:flex-col tw:gap-1 tw:px-4 tw:pt-1"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {liveTakeoverActive ? (
        <>
          <div
            role="status"
            aria-live="polite"
            className="tw:mx-auto tw:w-full tw:max-w-[768px] tw:overflow-hidden tw:rounded-2xl tw:border tw:bg-white tw:shadow-sm"
            style={{ borderColor: 'var(--bc-support-border)' }}
          >
            <div
              className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:px-3 tw:py-2"
              style={{
                backgroundColor: 'var(--bc-support-bg)',
                borderBottomColor: 'var(--bc-support-border)',
              }}
            >
              <span
                className="tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-sm tw:truncate"
                style={{ color: 'var(--bc-support-text)' }}
              >
                <span className="tw:relative tw:flex tw:h-1.5 tw:w-1.5 tw:shrink-0">
                  <span
                    className="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-ping tw:rounded-full tw:opacity-75"
                    style={{ backgroundColor: 'var(--bc-support-dot)' }}
                  />
                  <span
                    className="tw:relative tw:inline-flex tw:h-1.5 tw:w-1.5 tw:rounded-full"
                    style={{ backgroundColor: 'var(--bc-support-dot)' }}
                  />
                </span>
                {adminName ? (
                  <span className="tw:truncate">
                    Connected to <span className="tw:font-semibold">{adminName}</span>
                    <span className="tw:mx-1">·</span>
                    <span>{supportCopy.teamLabel}</span>
                  </span>
                ) : (
                  <span className="tw:truncate">
                    Connected to <span className="tw:font-semibold">{supportCopy.teamLabel}</span>
                  </span>
                )}
              </span>
              {onEndLiveAgent && (
                <button
                  type="button"
                  onClick={handleEnd}
                  disabled={endPending}
                  className="tw:shrink-0 tw:inline-flex tw:cursor-pointer tw:items-center tw:rounded-full tw:border tw:px-3 tw:py-1 tw:text-xs tw:font-semibold tw:transition-colors hover:tw:border-red-300 hover:tw:bg-red-50 hover:tw:text-red-600 disabled:tw:cursor-not-allowed disabled:tw:opacity-50"
                  style={{
                    borderColor: 'var(--bc-support-border)',
                    backgroundColor: 'var(--bc-support-bg)',
                    color: 'var(--bc-support-text)',
                  }}
                  aria-label={`Disconnect from ${supportCopy.teamLabel.toLowerCase()}`}
                >
                  {endPending ? 'Ending…' : 'Disconnect'}
                </button>
              )}
            </div>
            {promptInput}
          </div>
          <p className="tw:mx-auto tw:mt-1 tw:max-w-[768px] tw:text-center tw:text-xs tw:text-gray-500">
            {supportCopy.pausedFooterCopy}
          </p>
        </>
      ) : (
        promptInput
      )}
    </div>
  );
};
